// All-outlets (portfolio) financial dashboard.
//
// Only outlets where the signed-in user holds the Mobile-Dashboard role are
// included. Each outlet is loaded in parallel (bounded), so one offline or
// slow store never blocks the combined numbers.

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { DashboardResult } from '../lib/api.js';
import {
  DEFAULT_PERIOD,
  PERIOD_OPTIONS,
  accessibleDashboardStores,
  combineResults,
  fillMonths,
  formatDelta,
  formatMoney,
  formatPct,
  loadOutletDashboard,
  mapLimit,
  previousTotals,
  resolvePeriod,
  scoreHealth,
  sumMonths,
  type DashboardStoreInput,
  type DeltaValue,
  type HealthResult,
  type PeriodPreset,
  type PeriodTotals,
  type PeriodWindow,
} from '../lib/finance.js';
import { FINANCE_CHART_STYLES, renderFinanceChart } from './finance-chart.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

interface StoreRow {
  store: DashboardStoreInput;
  data?: DashboardResult;
  error?: string;
  totals?: PeriodTotals;
  health?: HealthResult;
}

type SortKey = 'name' | 'sales' | 'grossMargin' | 'expenses' | 'netProfit' | 'netMargin' | 'ar' | 'ap' | 'health';

@customElement('stores-dashboard')
export class StoresDashboard extends LitElement {
  static styles = [TAB_UI_STYLES, FINANCE_CHART_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .page-scroll { flex: 1; overflow-y: auto; min-height: 0; padding: 0 1rem 1.25rem; }
    .head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.75rem; }
    .head h2 { margin: 0 0 0.15rem; font-size: 1.1rem; }
    .sub { margin: 0; font-size: 0.76rem; color: var(--color-text-secondary); }
    .head-right { display: flex; flex-wrap: wrap; align-items: center; gap: 0.45rem; }
    .chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
    .chip { border: 1px solid var(--color-border); background: var(--color-surface); border-radius: 999px; padding: 6px 12px; font-family: inherit; font-size: 0.76rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary); }
    .chip:hover { border-color: var(--color-primary); color: var(--color-primary); }
    .chip.active { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }
    .refresh { border: 1px solid var(--color-border); background: var(--color-surface); border-radius: 10px; min-height: 34px; padding: 6px 10px; font-family: inherit; font-size: 0.76rem; cursor: pointer; color: var(--color-text-secondary); }
    .refresh:hover { border-color: var(--color-primary); color: var(--color-primary); }

    .state { text-align: center; padding: 2.5rem 1rem; color: var(--color-text-secondary); }
    .error { margin-bottom: 0.75rem; padding: 0.65rem 0.85rem; border-radius: 10px; background: #fdecea; color: #b71c1c; font-size: 0.84rem; }

    .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 0.65rem; margin-bottom: 0.85rem; }
    .kpi { border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); padding: 0.8rem 0.9rem; min-width: 0; }
    .kpi .label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-secondary); }
    .kpi .value { font-size: 1.22rem; font-weight: 800; margin-top: 0.25rem; overflow-wrap: anywhere; }
    .kpi .value.neg { color: var(--color-error, #d93025); }
    .kpi .sub-line { margin-top: 0.2rem; font-size: 0.72rem; color: var(--color-text-secondary); }
    .delta { display: inline-flex; align-items: center; gap: 0.15rem; margin-left: 0.35rem; font-size: 0.68rem; font-weight: 700; }
    .delta.good { color: var(--color-success, #188038); }
    .delta.bad { color: var(--color-error, #d93025); }
    .delta.flat { color: var(--color-text-secondary); }

    .panel { border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); padding: 0.9rem; margin-bottom: 0.85rem; }
    .panel h3 { margin: 0 0 0.6rem; font-size: 0.9rem; }

    .health { display: flex; gap: 1rem; align-items: center; }
    .ring { position: relative; width: 92px; height: 92px; flex-shrink: 0; }
    .ring svg { width: 92px; height: 92px; transform: rotate(-90deg); }
    .ring .bg { fill: none; stroke: #eef1f5; stroke-width: 9; }
    .ring .fg { fill: none; stroke-width: 9; stroke-linecap: round; transition: stroke-dashoffset 0.35s ease; }
    .health.healthy .ring .fg { stroke: var(--color-success, #188038); }
    .health.watch .ring .fg { stroke: var(--color-warning, #e8a317); }
    .health.risk .ring .fg { stroke: var(--color-error, #d93025); }
    .health.no-data .ring .fg { stroke: var(--color-border); }
    .ring .score { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; flex-direction: column; font-weight: 800; font-size: 1.35rem; }
    .ring .score small { font-size: 0.58rem; font-weight: 600; color: var(--color-text-secondary); }
    .health-copy h4 { margin: 0 0 0.2rem; font-size: 1rem; }
    .health-copy .band { display: inline-block; border-radius: 999px; padding: 2px 9px; font-size: 0.68rem; font-weight: 800; margin-bottom: 0.35rem; }
    .health.healthy .band { background: #e6f4ea; color: #137333; }
    .health.watch .band { background: #fff4d6; color: #8a5a00; }
    .health.risk .band { background: #fce8e6; color: #a50e0e; }
    .health.no-data .band { background: #eef1f5; color: var(--color-text-secondary); }
    .health-copy ul { margin: 0.15rem 0 0; padding-left: 1.05rem; font-size: 0.76rem; color: var(--color-text-secondary); }
    .health-copy li { margin-bottom: 0.15rem; }

    .ar-ap { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.65rem; }
    .mini-card { border: 1px solid var(--color-border); border-radius: 12px; padding: 0.7rem 0.8rem; }
    .mini-card .row { display: flex; justify-content: space-between; gap: 0.5rem; font-size: 0.78rem; margin-bottom: 0.25rem; }
    .mini-card .row strong { font-size: 0.95rem; }
    .mini-card .muted { font-size: 0.72rem; color: var(--color-text-secondary); }
    .mini-card .bar { height: 7px; border-radius: 999px; background: #eef1f5; overflow: hidden; margin-top: 0.35rem; }
    .mini-card .bar i { display: block; height: 100%; border-radius: 999px; background: var(--color-warning, #e8a317); }

    .table-wrap { overflow-x: auto; border: 1px solid var(--color-border); border-radius: 12px; background: var(--color-surface); }
    table { width: 100%; border-collapse: collapse; font-size: 0.78rem; min-width: 760px; }
    th, td { padding: 8px 10px; border-bottom: 1px solid var(--color-border); text-align: left; white-space: nowrap; }
    th { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-secondary); cursor: pointer; user-select: none; position: sticky; top: 0; background: var(--color-surface); z-index: 1; }
    th:hover { color: var(--color-primary); }
    th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
    tbody tr { cursor: pointer; }
    tbody tr:hover { background: #f7f9fd; }
    td.outlet { font-weight: 700; max-width: 200px; overflow: hidden; text-overflow: ellipsis; }
    td.err { color: var(--color-error, #d93025); font-size: 0.72rem; }
    .badge { display: inline-block; border-radius: 999px; padding: 2px 8px; font-size: 0.66rem; font-weight: 800; }
    .badge.healthy { background: #e6f4ea; color: #137333; }
    .badge.watch { background: #fff4d6; color: #8a5a00; }
    .badge.risk { background: #fce8e6; color: #a50e0e; }
    .badge.no-data { background: #eef1f5; color: var(--color-text-secondary); }

    .foot { margin-top: 0.5rem; font-size: 0.72rem; color: var(--color-text-secondary); display: flex; flex-wrap: wrap; gap: 0.5rem 1rem; }
  `];

  @property({ type: Array }) stores: DashboardStoreInput[] = [];

  @state() private _preset: PeriodPreset = DEFAULT_PERIOD;
  @state() private _window: PeriodWindow = resolvePeriod(DEFAULT_PERIOD);
  @state() private _rows: StoreRow[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _loadedAt = 0;
  @state() private _sortKey: SortKey = 'sales';
  @state() private _sortDir: 'asc' | 'desc' = 'desc';
  private _seq = 0;

  protected updated(changed: Map<PropertyKey, unknown>) {
    if (changed.has('stores')) {
      this._preset = DEFAULT_PERIOD;
      this._window = resolvePeriod(DEFAULT_PERIOD);
      void this._load();
    }
  }

  private _setPreset(preset: PeriodPreset) {
    if (this._preset === preset) return;
    this._preset = preset;
    this._window = resolvePeriod(preset);
    this._rows = [];
    void this._load();
  }

  private async _load() {
    const eligible = accessibleDashboardStores(this.stores || []);
    const seq = ++this._seq;
    this._error = '';
    this._loadedAt = 0;
    if (!eligible.length) {
      this._rows = [];
      this._loading = false;
      return;
    }
    this._loading = true;
    this._rows = eligible.map((store) => ({ store }));
    try {
      const rows = await mapLimit(eligible, 4, async (store) => {
        try {
          const data = await loadOutletDashboard(store.code, this._window);
          return { store, data } as StoreRow;
        } catch (e: any) {
          return { store, error: e?.message || 'Unavailable' } as StoreRow;
        }
      });
      if (seq !== this._seq) return;
      this._rows = rows;
      this._loadedAt = Date.now();
    } catch (e: any) {
      if (seq !== this._seq) return;
      this._error = e?.message || 'Failed to load the outlet overview';
    } finally {
      if (seq === this._seq) this._loading = false;
    }
  }

  private _deltaBadge(delta: DeltaValue | null, invert = false) {
    if (!delta) return nothing;
    const good = invert ? delta.direction === 'down' : delta.direction === 'up';
    const cls = delta.direction === 'flat' ? 'flat' : good ? 'good' : 'bad';
    const arrow = delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '';
    return html`<span class="delta ${cls}">${arrow} ${delta.text}</span>`;
  }

  private _toggleSort(key: SortKey) {
    if (this._sortKey === key) {
      this._sortDir = this._sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortKey = key;
      this._sortDir = key === 'name' ? 'asc' : 'desc';
    }
  }

  private _sortable() {
    return this._rows.map((row) => {
      const data = row.data;
      if (!data) return { row, name: row.store.name, sales: -1, grossMargin: -1, expenses: -1, netProfit: -1, netMargin: -1, ar: -1, ap: -1, health: -1 };
      const totals = sumMonths(data.months, this._window.from, this._window.to);
      const prev = previousTotals(data.months, this._window);
      const health = scoreHealth(totals, data.ar, data.ap, prev);
      return {
        row: { ...row, totals, health } as StoreRow,
        name: row.store.name,
        sales: totals.sales,
        grossMargin: totals.sales > 0 ? totals.grossProfit / totals.sales : 0,
        expenses: totals.expenses + totals.fees,
        netProfit: totals.netProfit,
        netMargin: totals.sales > 0 ? totals.netProfit / totals.sales : 0,
        ar: data.ar.total,
        ap: data.ap.total,
        health: health.score ?? -1,
      };
    }).sort((a, b) => {
      const av = a[this._sortKey] as string | number;
      const bv = b[this._sortKey] as string | number;
      const dir = this._sortDir === 'asc' ? 1 : -1;
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av).localeCompare(String(bv)) * dir;
      }
      return (Number(av) - Number(bv)) * dir;
    });
  }

  private _openOutlet(row: StoreRow) {
    if (!row.data) return;
    this.dispatchEvent(new CustomEvent('open-outlet', {
      detail: { code: row.store.code },
      bubbles: true,
      composed: true,
    }));
  }

  private _renderHealth(combined: ReturnType<typeof combineResults>) {
    const totals = sumMonths(combined.months, this._window.from, this._window.to);
    const prev = previousTotals(combined.months, this._window);
    const health = scoreHealth(totals, combined.ar, combined.ap, prev);
    const radius = 34;
    const circumference = 2 * Math.PI * radius;
    const progress = health.score == null ? 0 : Math.max(0, Math.min(100, health.score));
    return html`
      <div class="panel health ${health.band}" data-testid="portfolio-health">
        <div class="ring">
          <svg viewBox="0 0 92 92" aria-hidden="true">
            <circle class="bg" cx="46" cy="46" r=${radius}></circle>
            <circle class="fg" cx="46" cy="46" r=${radius}
              stroke-dasharray=${circumference.toFixed(1)}
              stroke-dashoffset=${(circumference - (progress / 100) * circumference).toFixed(1)}></circle>
          </svg>
          <div class="score">${health.score == null ? '—' : health.score}<small>/ 100</small></div>
        </div>
        <div class="health-copy">
          <span class="band">${health.label}</span>
          <h4>Combined financial health</h4>
          <ul>${health.reasons.map((r) => html`<li>${r}</li>`)}</ul>
        </div>
      </div>
    `;
  }

  private _renderBody() {
    const eligible = accessibleDashboardStores(this.stores || []);
    if (!eligible.length) {
      return html`
        <div class="state" data-testid="portfolio-no-access">
          No outlet has the <code>Mobile-Dashboard</code> role yet. Ask an outlet
          administrator to assign it before the combined dashboard can load.
        </div>`;
    }
    if (this._loading && !this._rows.some((r) => r.data)) {
      return html`<div class="state">Loading all outlets…</div>`;
    }
    if (this._error) return html`<div class="error">⚠ ${this._error}</div>`;

    const loaded = this._rows.filter((r) => r.data).map((r) => r.data as DashboardResult);
    const combined = combineResults(loaded);
    const totals = sumMonths(combined.months, this._window.from, this._window.to);
    const prev = previousTotals(combined.months, this._window);
    const grossMargin = totals.sales > 0 ? totals.grossProfit / totals.sales : 0;
    const netMargin = totals.sales > 0 ? totals.netProfit / totals.sales : 0;
    const costRatio = totals.sales > 0 ? (totals.expenses + totals.fees) / totals.sales : 0;
    const arOverdue = Math.max(0, combined.ar.d31_60 + combined.ar.d61_90 + combined.ar.d90p);
    const sorted = this._sortable();

    return html`
      ${this._renderHealth(combined)}
      <div class="kpi-grid">
        <div class="kpi">
          <div class="label">Revenue (net)</div>
          <div class="value">${formatMoney(totals.sales)}${this._deltaBadge(formatDelta(totals.sales, prev?.sales))}</div>
          <div class="sub-line">All loaded outlets</div>
        </div>
        <div class="kpi">
          <div class="label">Gross profit</div>
          <div class="value">${formatMoney(totals.grossProfit)}</div>
          <div class="sub-line">Gross margin ${formatPct(grossMargin)}</div>
        </div>
        <div class="kpi">
          <div class="label">Expenses + fees</div>
          <div class="value">${formatMoney(totals.expenses + totals.fees)}${this._deltaBadge(formatDelta(totals.expenses + totals.fees, prev ? prev.expenses + prev.fees : null), true)}</div>
          <div class="sub-line">${formatPct(costRatio)} of revenue</div>
        </div>
        <div class="kpi">
          <div class="label">Net profit</div>
          <div class="value ${totals.netProfit < 0 ? 'neg' : ''}">${formatMoney(totals.netProfit)}${this._deltaBadge(formatDelta(totals.netProfit, prev?.netProfit))}</div>
          <div class="sub-line">Net margin ${formatPct(netMargin)}</div>
        </div>
      </div>

      <div class="panel">
        <h3>Last 6 months</h3>
        ${renderFinanceChart(fillMonths(combined.months, this._window.chartFrom, this._window.to))}
      </div>

      <div class="panel">
        <h3>Receivables &amp; payables — as of today</h3>
        <div class="ar-ap">
          <div class="mini-card">
            <div class="row"><strong>Customer receivables</strong><strong>${formatMoney(combined.ar.total)}</strong></div>
            <div class="muted">${formatMoney(arOverdue)} over 30 days · ${combined.ar.parties} parties</div>
            <div class="bar"><i style="width:${combined.ar.total > 0 ? ((arOverdue / combined.ar.total) * 100).toFixed(1) : 0}%"></i></div>
          </div>
          <div class="mini-card">
            <div class="row"><strong>Supplier payables</strong><strong>${formatMoney(combined.ap.total)}</strong></div>
            <div class="muted">${combined.ap.parties} suppliers</div>
            <div class="bar"><i style="width:${combined.ap.total > 0 ? '100' : '0'}%"></i></div>
          </div>
        </div>
      </div>

      <div class="panel">
        <h3>Outlets</h3>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th @click=${() => this._toggleSort('name')}>Outlet${this._sortMarker('name')}</th>
                <th class="num" @click=${() => this._toggleSort('sales')}>Revenue${this._sortMarker('sales')}</th>
                <th class="num" @click=${() => this._toggleSort('grossMargin')}>Gross %${this._sortMarker('grossMargin')}</th>
                <th class="num" @click=${() => this._toggleSort('expenses')}>Expenses${this._sortMarker('expenses')}</th>
                <th class="num" @click=${() => this._toggleSort('netProfit')}>Net profit${this._sortMarker('netProfit')}</th>
                <th class="num" @click=${() => this._toggleSort('netMargin')}>Net %${this._sortMarker('netMargin')}</th>
                <th class="num" @click=${() => this._toggleSort('ar')}>AR${this._sortMarker('ar')}</th>
                <th class="num" @click=${() => this._toggleSort('ap')}>AP${this._sortMarker('ap')}</th>
                <th @click=${() => this._toggleSort('health')}>Health${this._sortMarker('health')}</th>
              </tr>
            </thead>
            <tbody>
              ${sorted.map(({ row }) => html`
                <tr data-testid="portfolio-outlet-row" data-outlet-code=${row.store.code}
                  tabindex="0"
                  @click=${() => this._openOutlet(row)}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._openOutlet(row); }
                  }}>
                  <td class="outlet" title=${row.store.name}>${row.store.name}</td>
                  ${row.data && row.totals ? html`
                    <td class="num">${formatMoney(row.totals.sales)}</td>
                    <td class="num">${formatPct(row.totals.sales > 0 ? row.totals.grossProfit / row.totals.sales : 0)}</td>
                    <td class="num">${formatMoney(row.totals.expenses + row.totals.fees)}</td>
                    <td class="num">${formatMoney(row.totals.netProfit)}</td>
                    <td class="num">${formatPct(row.totals.sales > 0 ? row.totals.netProfit / row.totals.sales : 0)}</td>
                    <td class="num">${formatMoney(row.data.ar.total)}</td>
                    <td class="num">${formatMoney(row.data.ap.total)}</td>
                    <td><span class="badge ${row.health?.band || 'no-data'}">${row.health?.score == null ? '—' : row.health.score}</span></td>
                  ` : html`
                    <td class="err" colspan="8">${row.error || 'Unavailable'}</td>
                  `}
                </tr>
              `)}
              ${!sorted.length ? html`<tr><td colspan="9" class="state">No outlets loaded.</td></tr>` : nothing}
            </tbody>
          </table>
        </div>
        <div class="foot">
          <span>${loaded.length} of ${eligible.length} outlets loaded</span>
          ${this.stores.length > eligible.length ? html`<span>${this.stores.length - eligible.length} outlet(s) without the dashboard role or expired</span>` : nothing}
          ${this._loadedAt ? html`<span>Updated ${new Date(this._loadedAt).toLocaleTimeString()}</span>` : nothing}
        </div>
      </div>
    `;
  }

  private _sortMarker(key: SortKey) {
    if (this._sortKey !== key) return nothing;
    return this._sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  render() {
    return html`
      <div class="page-scroll">
        <div class="head">
          <div>
            <h2>All outlets — financial health</h2>
            <p class="sub">Net of GST · Receivables/payables as of today · Mobile-Dashboard outlets only</p>
          </div>
          <div class="head-right">
            <div class="chips">
              ${PERIOD_OPTIONS.map((p) => html`
                <button class="chip ${this._preset === p.id ? 'active' : ''}"
                  data-testid="portfolio-period-${p.id}"
                  @click=${() => this._setPreset(p.id)}>${p.label}</button>
              `)}
            </div>
            <button class="refresh" ?disabled=${this._loading} @click=${() => this._load()}>
              ${this._loading ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>
        </div>
        ${this._renderBody()}
      </div>
    `;
  }
}
